import "virtual:uno.css";
import { App as CapacitorApp, type URLOpenListenerEvent } from "@capacitor/app";
import * as Sentry from "@sentry/browser";
import { MetaProvider } from "@solidjs/meta";
import { useNavigate } from "@solidjs/router";
import { Route, Router } from "@solidjs/router";
import {
	ErrorBoundary,
	type Owner,
	Show,
	createRoot,
	createSignal,
	getOwner,
	onMount,
} from "solid-js";
import toast, { Toaster } from "solid-toast";
import "tippy.js/animations/shift-away.css";
import "tippy.js/dist/tippy.css";
import "~/global.css";
import { CARDS_STATE, quick, setOwner } from "~/utils/app_state";
import { AccountDeletionView } from "./components/AccountDeletion";
import { AdminView } from "./components/AdminView";
import AuthHandler from "./components/AuthHandler";
import { ChessMadra } from "./components/ChessMadra";
import { Confetti } from "./components/Confetti";
import ForgotPassword from "./components/ForgotPassword";
import { LoginSidebar } from "./components/LoginSidebar";
import { MoveAnnotationsDashboard } from "./components/MoveAnnotationsDashboard";
import { PageWrapper } from "./components/PageWrapper";
import ResetPassword from "./components/ResetPassword";
import { ReviewMoveAnnotationsView } from "./components/ReviewMoveAnnotationsView";
import { SpoofWarning } from "./components/SpoofWarning";
import DesignSytem from "./pages/DesignSystem";
import LichessOAuthCallback from "./pages/LichessOAuthCallback";
// import { setShouldPlaySounds } from "./utils/chessboard_interface";
import { createDebugStateEffect } from "./utils/debug_effect";
import { isChessbook, isChesscards, isChessmadra, isDevelopment, isNative } from "./utils/env";
import { InAppPurchases } from "./utils/in_app_purchases";
import { Notifications } from "./utils/notifications";
import { loadStockfish } from "./utils/stockfish";
import "solid-devtools";
import { Capacitor } from "@capacitor/core";
import { AppRedirect } from "./components/AppRedirect";
import { EcoCodesDashboard } from "./components/EcoCodesDashboard";
import { ServerAvailabilityChecker } from "./components/ServerAvailabilityChecker";
import { TestErrorView } from "./components/TestErrorView";
import { WebsocketHandler } from "./components/WebsocketHandler";
import { FakeAnalysis } from "./pages/FakeAnalysis";
import { initializePosthog } from "./utils/posthog";
import { trackEvent } from "./utils/trackEvent";

// this suck but it is what it is I guess
// React uses process.env, define doesn't do it because it's in the library code or something I don't know
window.process = window.process ?? {};
window.process.env = process.env;

const [errorResets, setErrorResets] = createSignal(0);

const _findRootOwner = (owner: Owner) => {
	let rootOwner = owner;
	while (rootOwner.owner) {
		rootOwner = rootOwner.owner;
	}
	return rootOwner;
};
const InnerApp = () => {
	return createRoot(() => {
		setOwner(getOwner()!);
		return <App />;
	});
};

export default InnerApp;

export const App = () => {
	// setOwner(findRootOwner(getOwner()!));
	// setInterval(() => {
	// 	playSound(SOUNDS.move);
	// }, 1000);
	onMount(() => {
		initializePosthog();
		if (isNative) {
			InAppPurchases.loadProducts();
			Notifications.addListeners();
		}

		Sentry.init({
			environment: isDevelopment ? "development" : "production",
			dsn: isDevelopment
				? "https://5c4df4321e7b4428afef85ec9f08cbd1@o1268497.ingest.sentry.io/6456185"
				: "https://5c4df4321e7b4428afef85ec9f08cbd1@o1268497.ingest.us.sentry.io/6456185",
			beforeSend: (event) => {
				const failedToFetchError = event?.exception?.values?.some(
					(exception) =>
						exception.type === "TypeError" &&
						(exception.value?.includes("Failed to fetch") ||
							exception.value?.includes("Load failed")),
				);
				const axiosError = event?.exception?.values?.some(
					(exception) =>
						exception.type === "AxiosError" &&
						(exception.value?.includes("Network Error") ||
							exception.value?.includes("Request aborted")),
				);

				if (axiosError) {
					return null;
				}

				if (failedToFetchError) {
					return null;
				}

				if (event.extra) {
					event.extra.platform = Capacitor.getPlatform();
				}
				return event;
			},
			integrations: [
				// new BrowserTracing()
			],

			debug: !(!process.env.NODE_ENV || process.env.NODE_ENV === "development"),
			// We recommend adjusting this value in production, or using tracesSampler
			// for finer control
			tracesSampleRate: 1.0,
			beforeBreadcrumb: (breadcrumb, hint) => {
				if (breadcrumb.category === "xhr") {
					// @ts-expect-error
					const xhr = hint.xhr as XMLHttpRequest;
					const data = {
						// @ts-expect-error
						requestBody: xhr.__sentry_xhr__?.body,
						responseCode: xhr.status,
						response: xhr.response,
						responseUrl: xhr.responseURL,
					};
					return { ...breadcrumb, data };
				}
				return breadcrumb;
			},
			// ...
			// Note: if you want to override the automatic release value, do not set a
			// `release` value here - use the environment variable `SENTRY_RELEASE`, so
			// that it will also get attached to your source maps
		});
	});
	createDebugStateEffect();
	onMount(() => {
		setTimeout(() => {
			loadStockfish();
		}, 2000);
	});
	return (
		<ErrorBoundary
			fallback={(error, reset) => {
				if (errorResets() === 0) {
					Sentry.captureException(error);
				}
				trackEvent("error.unhandled", { message: error.message });
				if (isDevelopment && errorResets() === 0) {
					console.error(error);
					toast.error(`error: ${error.message}`);
					// biome-ignore lint: debugger is fine here
					debugger;
				}
				if (errorResets() < 5) {
					setErrorResets(errorResets() + 1);
					reset();
				}
				return (
					<div class="flex flex-col items-center justify-center min-h-screen">
						<div class="max-w-md w-full space-y-8 p-6 bg-white rounded-lg shadow-md my-8 max-h-[600px] flex">
							<h2 class="text-3xl font-extrabold text-red-600">Unexpected Error</h2>
							<p class="text-gray-600">{JSON.stringify(error.message)}</p>
							<div class="bg-gray-100 p-4 rounded-md flex-shrink min-h-0 overflow-y-scroll">
								<pre class="text-sm text-gray-30 text-wrap break-all">
									{JSON.stringify(error.stack)}
								</pre>
							</div>
							<button
								class="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
								use:onClick={() => window.location.reload()}
							>
								Reload Page
							</button>
						</div>
					</div>
				);
			}}
		>
			<AuthHandler>
				<Show when={isChesscards}>
					<WebsocketHandler />
				</Show>
				<div class="toast-container">
					<Toaster position="bottom-right" gutter={24} />
				</div>
				<SpoofWarning />
				<Show when={isDevelopment}>
					<ServerAvailabilityChecker />
				</Show>
				<Confetti />
				<MetaProvider />
				<Router root={(props) => <RouteWrapper {...props} />}>
					<Route path="/" component={() => (isChessmadra ? <ChessMadra /> : <PageWrapper />)} />
					<Route path="/admin/move-annotations/community" component={ReviewMoveAnnotationsView} />
					<Route path="/admin/move-annotations" component={MoveAnnotationsDashboard} />
					<Route path="/admin/eco-codes" component={EcoCodesDashboard} />
					<Route path="/admin" component={AdminView} />
					<Route path="/oauth/lichess" component={LichessOAuthCallback} />
					<Route path="/design-system" component={DesignSytem} />
					<Route
						path="/forgot-password"
						component={() => <PageWrapper initialView={ForgotPassword} />}
					/>
					<Route path="/fake-analysis" component={FakeAnalysis} />
					<Route
						path="/reset-password"
						component={() => <PageWrapper initialView={ResetPassword} />}
					/>
					<Route path="/login" component={() => <PageWrapper initialView={LoginSidebar} />} />
					<Route path="/test-error" component={TestErrorView} />
					<Route
						path="/account-deletion"
						component={() => <PageWrapper initialView={AccountDeletionView} />}
					/>
					<Route path="/vision" component={() => <ChessMadra initialTool="vision" />} />
					<Route path="/qr" component={() => <AppRedirect />} />
					<Route
						path="/blunder-puzzles"
						component={() => <ChessMadra initialTool="blunder-puzzles" />}
					/>
					<Route
						path="/visualization"
						component={() => <ChessMadra initialTool="visualization" />}
					/>
					<Route path="/*" component={() => (isChessmadra ? <ChessMadra /> : <PageWrapper />)} />
				</Router>
			</AuthHandler>
		</ErrorBoundary>
	);
};

const RouteWrapper = (props: { children?: any } & unknown) => {
	return (
		<>
			<RouteProvider />
			<AppUrlListener />
			{props.children}
		</>
	);
};

const RouteProvider = () => {
	const navigate = useNavigate();
	onMount(() => {
		quick((s) => {
			s.navigationState.setNavigate(navigate);
		});
	});
	return null;
};
const AppUrlListener = () => {
	const navigate = useNavigate();
	let lastResume = Date.now();
	onMount(() => {
		CapacitorApp.addListener("backButton", () => {
			quick((s) => {
				s.ui.backButtonCallback.call();
			});
		});
		CapacitorApp.addListener("resume", () => {
			// (async () => {
			// 	const playingAudio = (await AudioPlayingChecker.isPlayingAudio()).playing;
			// 	setShouldPlaySounds(!playingAudio);
			// })();
			// if over 60 seconds since last resume
			if (Date.now() - lastResume > 60 * 1000) {
				lastResume = Date.now();
				if (isChessbook) {
					quick((s) => {
						s.repertoireState.fetchLichessMistakes();
					});
				} else if (isChesscards) {
					CARDS_STATE().fetchCards();
				}
			}
		});
		if (isNative) {
			CapacitorApp.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
				const url = new URL(event.url);

				// Concatenate the pathname (route) and search (query params)
				const route = url.pathname + url.search;
				navigate(route);

				// Example url: https://beerswift.app/tabs/tab2
				// slug = /tabs/tab2
				// const slug = event.url.split(".com").pop();
				// if (slug) {
				// 	history.push(slug);
				// }
				// If no match, do nothing - let regular routing
				// logic take over
			});
		}
	});

	return null;
};
